import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { BlogHeader, BlogFooter } from "../components/blog/BlogHeader";
import { SignupCTA } from "../components/blog/SignupCTA";
import { CompareTable } from "../components/compare/CompareTable";
import { CHECKED, RIVALS } from "../data/compareData";

const PRICES = [
  { name: "Trackr Pro", price: "$6", per: "/month", alt: "or $19 once, for life", highlight: true },
  { name: "Huntr Pro", price: "$40", per: "/month", alt: "discounts on 3 and 6 month plans" },
  { name: "Teal+", price: "$29", per: "/month", alt: "weekly and quarterly options" },
  { name: "Spreadsheet", price: "$0", per: "", alt: "plus your time building it" },
];

export default function Compare() {
  usePageMeta(
    "Trackr vs. Spreadsheets, Huntr, Teal & Notion — Job Tracker Comparison",
    "Compare Trackr with Google Sheets, Excel, Huntr, Teal and Notion templates on price, features and exports. Find the best job application tracker for you."
  );
  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10">
        <section className="pt-16 pb-12 max-w-3xl">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">Compare</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium mt-4" data-testid="compare-title">
            The job tracker that does more, <span className="text-[#8B9D83]">for less.</span>
          </h1>
          <p className="text-base text-[#5C6B64] mt-6 leading-relaxed">
            An honest look at Trackr next to spreadsheets, Huntr, Teal and Notion templates, including where the others win.
          </p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="compare-prices">
          {PRICES.map((p) => (
            <div key={p.name} className={`rounded-xl p-6 border ${p.highlight ? "bg-[#1A362D] text-[#F4F1EE] border-[#1A362D]" : "bg-white border-[#E5E1DB]"}`}>
              <p className={`text-xs tracking-[0.2em] uppercase font-semibold ${p.highlight ? "text-[#D99C3D]" : "text-[#5C6B64]"}`}>{p.name}</p>
              <p className="font-display text-4xl mt-3">{p.price}<span className="text-base opacity-70">{p.per}</span></p>
              <p className={`text-xs mt-2 ${p.highlight ? "text-[#F4F1EE]/75" : "text-[#5C6B64]"}`}>{p.alt}</p>
            </div>
          ))}
        </section>

        <CompareTable />
        <p className="text-xs text-[#5C6B64] mt-4" data-testid="compare-disclaimer">
          Competitor details are taken from their public pricing pages and product info as of {CHECKED} and may change, so check their sites for current offers. "Partial" means the feature exists in a limited form. All trademarks belong to their owners.
        </p>

        <section className="mt-20">
          <h2 className="font-display text-3xl tracking-tight mb-8">Head-to-head comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.values(RIVALS).map((r) => (
              <Link key={r.slug} to={`/compare/${r.slug}`} className="group rounded-xl border border-[#E5E1DB] bg-white p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5" data-testid={`compare-card-${r.slug}`}>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">Trackr vs.</p>
                <h3 className="font-display text-2xl mt-2 flex items-center justify-between group-hover:text-[#C65D47] transition-colors">
                  {r.long} <ArrowUpRight className="w-5 h-5" />
                </h3>
                <p className="text-sm text-[#5C6B64] mt-3 leading-relaxed">{r.verdict}</p>
              </Link>
            ))}
          </div>
        </section>
        <SignupCTA variant="end" />
      </main>
      <BlogFooter />
    </div>
  );
}
